import React, { Fragment, useEffect, useState } from 'react'
import { Button, Grid, withStyles } from '@material-ui/core';
import styles from './styles'
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DragDropContext } from 'react-beautiful-dnd';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlaceOutlined as MarkerIcon,
  PlaylistAdd as AddMultipleIcon,
  ControlPointDuplicate as DuplicateIcon,
} from '@material-ui/icons';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { getDayNumberByDayOfWeek } from 'services/enums/dayOfWeekHelper';
import { DndHelperElementTypes, DndHelperService } from 'services/dndHelper/dndHelperService';
import { presetsService } from 'services/presets/presetService';
import { displayModes } from 'services/enums/displayModes';
import { TemplateStopService } from 'services/backend/templateStopService';
import {TemplateAggregationCard, TemplateStopTemplateMap} from 'components';
import TemplateTable from '../TemplateTable';
import TemplateStopTable from '../TemplateStopTable';
import EditTemplateDialog from '../EditTemplateDialog';
import AddTemplateStopsDialog from '../AddTemplateStopsDialog';
import CreateTemplateDialog from '../CreateTemplateDialog';
import {GoogleMapsDistanceMatrixModeOption} from "../../../services/enums/GoolgeMapsDistanceMatrixHelper";
import {
  TemplateStopTemplateModeOption
} from "../../../services/enums/TemplateStopTemplateAlgorithmTypeHelper";

function TemplateOverview(props) {

  const {
    classes,
    className,
    t,
    templates,
    setTemplates,
    filter,
    carrierWithMicroHubs,
    reloadTemplates,
    shipperOptions,
    displayMode,
    templateStops
  } = props;

  const rootClassName = classNames(classes.root, className);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [displayUnassignedTemplateStops, setDisplayUnassignedTemplateStops] = useState(true);
  const [createTemplateDialogState, setCreateTemplateDialogState] = useState({ open: false });
  const [editTemplateDialogState, setEditTemplateDialogState] = useState({ open: false });
  const [addTemplateStopsToTemplateDialogState, setAddTemplateStopsToTemplateDialogState] = useState({ open: false });
  const [unassignedTemplateStopsToDisplayOnMap, setUnassignedTemplateStopsToDisplayOnMap] = useState([]);
  const [showOnlySelectedTemplate, setShowOnlySelectedTemplate] = useState(false);
  const [templatePreset, setTemplatePreset] = useState({});
  const [mapState, setMapState] = useState({ center: [50, 10.5], zoom: 6 });

  useEffect(() => {
    const newTemplatePreset = presetsService.getTemplateStopTemplatePresetWithCarrierMicroHubDayOfWeekAndShipper(
      filter.carrierName,
      filter.microHubName,
      filter.dayOfWeek,
      filter.shipperName, 
      new Date(Date.now() + 60 * 60 * 1000), //default datetime today + 2 hours
      GoogleMapsDistanceMatrixModeOption.Bicycling,
      TemplateStopTemplateModeOption.Manual,
      new Date(Date.now())  
    );
    setTemplatePreset(newTemplatePreset)
  }, [filter.dayOfWeek, filter.shipperName, filter.microHubName, filter.carrierName]);

  useEffect(() => {
    setSelectedTemplate(prevState => {
      if (prevState === null) return prevState;
      const selectedTemplateIndex = templates.findIndex(t => t.id === prevState.id);
      return selectedTemplateIndex >= 0 ? templates[selectedTemplateIndex] : null;
    })
  }, [templates]);

  useEffect(() => {
    const templateStopsOnTemplates = showOnlySelectedTemplate ? selectedTemplate?.stops?.map(s => s.id) || [] :
      templates.map(t => t.stops.map(s => s.id)).flat();
    const templateStopsNotOnTemplates = templateStops.filter(templateStop => !templateStopsOnTemplates.includes(templateStop.id));
    setUnassignedTemplateStopsToDisplayOnMap(templateStopsNotOnTemplates);
  }, [templateStops, templates, showOnlySelectedTemplate, selectedTemplate]);

  const onSelectStopOnMapFromTemplate = (stop) => {
    const template = templates.filter(t => t.stops.map(s => s.id).includes(stop.id))[0];
    setSelectedTemplate(template);
  };

  const onDragEnd = (event) => {
    if (!event.destination) {
      return;
    }

    if (event.destination === event.source) {
      return;
    }

    const source = DndHelperService.parseUniqueId(event.source.droppableId);
    const destination = DndHelperService.parseUniqueId(event.destination.droppableId);
    const element = DndHelperService.parseUniqueId(event.draggableId);

    if (element.type === DndHelperElementTypes.Tour) {
      // Template was moved in template table
      onReorderTemplates(event.source.index, event.destination.index);
    } else if (element.type === DndHelperElementTypes.Stop && source.tourId === destination.tourId) {
      // Template stop was moved on same template
      onReorderStopsOnTemplate(element.tourId, event.source.index, event.destination.index);
    } else if (element.type === DndHelperElementTypes.Stop && source.tourId !== destination.tourId) {
      // Template stop was moved to another template
      onMoveStopToOtherTemplate(element.stopId, source.tourId, destination.tourId);
    } else {
      console.warn('Unknown or unhandled Drag & Drop event', event);
    }
  };

  const onReorderTemplates = async (sourceIndex, destinationIndex) => {
    if (sourceIndex === destinationIndex) return;
    const reorderedTemplates = DndHelperService.reorder(templates, sourceIndex, destinationIndex);
    reorderedTemplates.forEach((template, index) => {
      template.templateNumber = index + 1
    })
    const reorderedTemplateIds = reorderedTemplates.map(x => x.id);
    setTemplates(reorderedTemplates);
    try {
      await TemplateStopService.reorderTemplates(reorderedTemplateIds, filter);
    } catch (error) {
      alert(`${t('errorReorderingTourTemplates')}, ${error}`);
      reloadTemplates();
    }
  };

  const onReorderStopsOnTemplate = async (templateId, sourceIndex, destinationIndex) => {
    if (sourceIndex === destinationIndex) return;
    const reorderedStops = DndHelperService.reorder(selectedTemplate.stops, sourceIndex, destinationIndex);
    const reorderedStopIds = reorderedStops.map(x => x.id);
    try {
      const updatedTemplate = await TemplateStopService.reorderTemplateStops(templateId, reorderedStopIds);
      const newTemplates = JSON.parse(JSON.stringify(templates));
      const templateIndex = newTemplates.findIndex(t => t.id === templateId);
      if (templateIndex >= 0) {
        newTemplates[templateIndex] = updatedTemplate;
        setTemplates(newTemplates);
      }
    } catch (error) {
      alert(`${t('errorReorderingStopsOfTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };

  const onMoveStopToOtherTemplate = async (stopId, sourceTemplateId, targetTemplateId) => {
    if (sourceTemplateId === targetTemplateId) return;
    try {
      await TemplateStopService.moveTemplateStopToTemplate(stopId, sourceTemplateId, targetTemplateId);
      reloadTemplates(false);
    } catch (error) {
      alert(`${t('errorMovingStopToOtherTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };

  const onCreateTemplate = async (template) => {
    setCreateTemplateDialogState(prevState => ({ ...prevState, open: false }));
    try {
      const templateToCreate = JSON.parse(JSON.stringify(template))
      templateToCreate.dayOfWeek = getDayNumberByDayOfWeek(template.dayOfWeek); // Change day of week to match backend enum
      const newTemplate = await TemplateStopService.createTemplate(templateToCreate, filter);
      newTemplate.stops = [];
      if (!templateMatchesFilter(newTemplate)) {
        return;
      }
      const newTemplates = JSON.parse(JSON.stringify(templates));
      newTemplates.push(newTemplate);
      setTemplates(newTemplates);
    } catch (error) {
      alert(`${t('errorCreatingTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };

  const templateMatchesFilter = (template) => {
    if (getDayNumberByDayOfWeek(filter.dayOfWeek) !== template.dayOfWeek) return false;
    if (filter.shipperName !== template.shipperName) return false;
    if (filter.carrierName && (filter.carrierName !== template.carrierName)) return false;
    if (filter.microHubName && (template.microHubName !== filter.microHubName)) return false;

    return true;
  }

  const onDeleteTemplate = async (template) => {
    try {
      // manually update for faster UI response time
      const newTemplates = JSON.parse(JSON.stringify(templates));
      const index = newTemplates.findIndex(x => x.id === template.id);
      if (index >= 0) {
        newTemplates.splice(index, 1);
        setTemplates(newTemplates)
      }
      // call backend and reload to get right priorities for all loaded templates
      await TemplateStopService.deleteTemplate(template.id);
      reloadTemplates(false);
    } catch (error) {
      alert(`${t('errorDeletingTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };

  const onEditTemplate = async (template) => {
    setEditTemplateDialogState({ ...editTemplateDialogState, open: false });
    try {
      // manually update for faster UI response time
      const newTemplates = JSON.parse(JSON.stringify(templates));
      const index = newTemplates.findIndex(x => x.id === template.id);
      if (index >= 0) {
        newTemplates[index] = template;
        setTemplates(newTemplates)
      }
      await TemplateStopService.updateTemplate(template);
    } catch (error) {
      alert(`${t('errorUpdatingTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };

  const onDuplicateTemplate = async (template) => {
    try {
      var filterForBackend = Object.assign({}, filter, { dayOfWeek: getDayNumberByDayOfWeek(filter.dayOfWeek) });
      const newTemplate = await TemplateStopService.duplicateTemplate(template.id, filterForBackend);
      const newTemplates = JSON.parse(JSON.stringify(templates));
      newTemplates.push(newTemplate);
      setSelectedTemplate(newTemplate);
      setTemplates(newTemplates);
    } catch (e) {
      alert(`${t('errorDuplicatingTemplate')}, ${e}`);
      reloadTemplates();
    }
  };

  const onAddTemplateStopsToTemplate = async (templateStops, template) => {
    setAddTemplateStopsToTemplateDialogState({ ...addTemplateStopsToTemplateDialogState, open: false });
    try {
      const updatedTemplate = await TemplateStopService.addTemplateStopsToTemplate(template.id, templateStops.map(templateStop => templateStop.id));
      const newTemplates = JSON.parse(JSON.stringify(templates));
      const index = newTemplates.findIndex(x => x.id === template.id);
      if (index >= 0) {
        newTemplates[index] = updatedTemplate;
        setTemplates(newTemplates);
      }
    } catch (error) {
      alert(`${t('errorAddingCustomersToTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };

  const onDeleteStopFromTemplate = async (stopId, template) => {
    try {
      const updatedTemplate = await TemplateStopService.deleteTemplateStopFromTemplate(template.id, stopId);
      const newTemplates = JSON.parse(JSON.stringify(templates));
      const index = newTemplates.findIndex(x => x.id === template.id);
      if (index >= 0) {
        newTemplates[index] = updatedTemplate;
        setTemplates(newTemplates);
      }
    } catch (error) {
      alert(`${t('errorDeletingStopFromTourTemplate')}, ${error}`);
      reloadTemplates();
    }
  };
  
  const calculateTemplatesLengthAndDuration = async () => {
    try {
      const templateIds = templates.map(t => t.id);
      const updatedTemplates = await TemplateStopService.RecalculateTemplatesDistanceAndDuration(templateIds);
      setTemplates(updatedTemplates);
    } catch (error) {
      alert(`${t('errorCalculatingTemplatesLengthAndDuration')}, ${error}`)
    }
  }

  return (
    <Fragment>
      <FormControlLabel
        className={classes.displayUnassignedTemplateStopsCheckbox}
        control={
          <Checkbox
            checked={displayUnassignedTemplateStops}
            color="secondary"
            onChange={(event) => setDisplayUnassignedTemplateStops(event.target.checked)}
            value={displayUnassignedTemplateStops}
          />
        }
        label={
          <Fragment>
            <MarkerIcon
              className={classes.unassignedTemplateStopMarkerIcon}
            />
            {t('displayCustomers')}
          </Fragment>
        }
      />
      <FormControlLabel
        className={classes.displayUnassignedTemplateStopsCheckbox}
        control={
          <Checkbox
            checked={showOnlySelectedTemplate}
            color="secondary"
            onChange={(event) => setShowOnlySelectedTemplate(event.target.checked)}
            value={showOnlySelectedTemplate}
          />
        }
        label={t('showOnlySelectedTourTemplate')}
      />
      <Grid
        className={rootClassName}
        container
        spacing={1}
      >
        <Grid
          item
          lg={12}
          sm={12}
          xl={6}
          xs={12}
        >
          <TemplateStopTemplateMap
            className={classes.gridItem}
            displayControls
            displayUnassignedTemplateStops={displayUnassignedTemplateStops}
            mapState={mapState}
            onSelectStop={onSelectStopOnMapFromTemplate}
            setMapState={setMapState}
            templates={showOnlySelectedTemplate ? selectedTemplate ? [selectedTemplate] : [] : templates}
            unassignedTemplateStops={unassignedTemplateStopsToDisplayOnMap}
          />
        </Grid>
        <Grid
          item
          lg={12}
          sm={12}
          xl={6}
          xs={12}
        >
          <div
            className={classes.gridItem}
          >
            <TemplateAggregationCard
                templates={templates}
            />
            <br />
            <DragDropContext
              onDragEnd={onDragEnd}
            >
              <TemplateTable
                className={classes.templateTable}
                displayMode={displayMode}
                filter={filter}
                selectedDayOfWeek={filter.dayOfWeek}
                selectedTemplate={selectedTemplate}
                selectTemplate={setSelectedTemplate}
                templates={templates}
              />
              <Button
                className={classes.button}
                color="secondary"
                onClick={() => setCreateTemplateDialogState({ ...createTemplateDialogState, open: true })}
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
              >
                {t('createTemplate')}
              </Button>
              <Button
                  className={classes.button}
                  onClick={() => calculateTemplatesLengthAndDuration()}
                  size="small"
                  variant="contained"
              >
                {t('CalculateAllTemplatesLengthAndDuration')}
              </Button>
              <br />
              {selectedTemplate &&
                <Fragment>
                  <TemplateStopTable
                    className={classes.templateStopTable}
                    deleteStopFromTemplate={(stopId) => onDeleteStopFromTemplate(stopId, selectedTemplate)}
                    displayMode={displayMode}
                    template={selectedTemplate}
                  />
                  <Button
                    className={classes.button}
                    color="primary"
                    onClick={() => setAddTemplateStopsToTemplateDialogState({
                      ...addTemplateStopsToTemplateDialogState,
                      open: true
                    })}
                    size="small"
                    title={t('addCustomersToTemplate')}
                    variant="contained"
                  >
                    <AddMultipleIcon />
                  </Button>
                  <Button
                    className={classes.button}
                    color="primary"
                    onClick={() => setEditTemplateDialogState({ ...editTemplateDialogState, open: true })}
                    size="small"
                    title={t('editTourTemplate')}
                    variant="contained"
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    className={classes.button}
                    color="primary"
                    onClick={() => onDuplicateTemplate(selectedTemplate)}
                    size="small"
                    title={t('duplicateTemplate')}
                    variant="contained"
                  >
                    <DuplicateIcon />
                  </Button>
                  <Button
                    className={classNames(classes.button, classes.redButton)}
                    onClick={() => onDeleteTemplate(selectedTemplate)}
                    size="small"
                    variant="contained"
                  >
                    <DeleteIcon />&nbsp;{t('deleteTemplate')}
                  </Button>
                  <EditTemplateDialog
                    dialogState={editTemplateDialogState}
                    handleCancel={() => setEditTemplateDialogState({ ...editTemplateDialogState, open: false })}
                    handleClose={onEditTemplate}
                    templateToEdit={selectedTemplate}
                  />
                  <AddTemplateStopsDialog
                    dialogState={addTemplateStopsToTemplateDialogState}
                    displayMode={displayMode}
                    handleCancel={() => setAddTemplateStopsToTemplateDialogState({
                      ...addTemplateStopsToTemplateDialogState,
                      open: false
                    })}
                    handleClose={onAddTemplateStopsToTemplate}
                    parentMapState={mapState}
                    selectedTemplate={selectedTemplate}
                    setParentMapState={setMapState}
                    templates={templates}
                    templateStops={templateStops}
                  />
                </Fragment>
              }
            </DragDropContext>
          </div>
        </Grid>
      </Grid>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <CreateTemplateDialog
        carrierWithMicroHubs={carrierWithMicroHubs}
        dialogState={createTemplateDialogState}
        handleCancel={() => setCreateTemplateDialogState({ createTemplateDialogState, open: false })}
        handleClose={onCreateTemplate}
        shipperOptions={shipperOptions}
        templatePreset={templatePreset}
      />
      </div>
    </Fragment>
  );
}


TemplateOverview.propTypes = {
  carrierWithMicroHubs: PropTypes.object,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  displayMode: PropTypes.oneOf(Object.values(displayModes)).isRequired,
  filter: PropTypes.object.isRequired,
  reloadTemplates: PropTypes.func.isRequired,
  setTemplates: PropTypes.func.isRequired,
  shipperOptions: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
  templateStops: PropTypes.array,
};

export default compose(withStyles(styles), withTranslation())(TemplateOverview);
